<template>
  <li v-show="isMenuItemVisible">
    <div v-if="hasSubMenu" class="side-menu-section">
      <span>{{ $t(`SIDEBAR.${menuItem.label}`) }}</span>
      <woot-button
        v-if="menuItem.showNewButton"
        size="tiny"
        variant="clear"
        color-scheme="secondary"
        icon="add"
        class="p-0"
        @click="onClickOpen"
      />
    </div>
    <template v-if="!hasSubMenu">
      <a
        v-if="menuItem.toStateName === 'email_campaigns'"
        :href="menuItem.externalUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="side-menu-child"
        :class="computedClass"
      >
        <nav-icon v-if="menuItem.iconSrc" :src="menuItem.iconSrc" :size="16" />
        <fluent-icon v-else :icon="menuItem.icon" size="16" />
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </a>
      <router-link
        v-else
        class="side-menu-child"
        :class="computedClass"
        :to="menuItem && menuItem.toState"
        @click.native="menuItem.onClick && menuItem.onClick()"
      >
        <nav-icon v-if="menuItem.iconSrc" :src="menuItem.iconSrc" :size="16" />
        <fluent-icon v-else :icon="menuItem.icon" size="16" />
        <span class="truncate">{{ $t(`SIDEBAR.${menuItem.label}`) }}</span>
        <span
          v-if="menuItem.count !== 0 && showChildCount(menuItem.count)"
          class="side-menu-count"
          :class="{ 'is-zero': isCountZero }"
        >
          {{ `${menuItem.count}` }}
        </span>
        <span
          v-if="menuItem.beta"
          data-view-component="true"
          label="Beta"
          class="px-1 inline-block font-medium leading-4 border border-green-400 text-green-500 rounded-lg text-xxs"
        >
          {{ $t('SIDEBAR.BETA') }}
        </span>
      </router-link>
    </template>

    <ul v-if="hasSubMenu" class="side-menu-children">
      <secondary-child-nav-item
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
      />
      <router-link
        v-if="showItem(menuItem)"
        v-slot="{ href, navigate }"
        :to="menuItem.toState"
        custom
      >
        <li>
          <a
            :href="href"
            class="side-menu-child"
            @click="e => newLinkClick(e, navigate)"
          >
            <fluent-icon icon="add" size="16" />
            {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
          </a>
        </li>
      </router-link>
    </ul>
  </li>
</template>

<script>
import { mapGetters } from 'vuex';

import adminMixin from '../../../mixins/isAdmin';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import NavIcon from './NavIcon.vue';
import {INBOX_TYPES} from "shared/mixins/inboxMixin";
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

export default {
  components: { SecondaryChildNavItem, NavIcon },
  mixins: [adminMixin, hasPermissionMixin],
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass === 'is-active';
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }
      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }
      return true;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name.includes(this.menuItem.toStateName);
    },

    computedClass() {
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox) return '';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isCurrentRoute
      ) {
        return 'is-active';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'is-active';
        }
        return '';
      }

      return '';
    },
  },
  methods: {

    computedInboxClass(child) {
      let { type, phoneNumber, apiType} = child;
      if (apiType) type = `${type}_${apiType}`; // d99d
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('add-label');
        }
      }
    },
    showItem(item) {
      if (item.key === 'label') {
        return this.hasCreatePermission('Labels') && !!item.newLink;
      }
      if (item.key === 'inbox') {
        return this.hasCreatePermission('Inbox') && !!item.newLink;
      }
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>

<style lang="scss" scoped>

// d99d
.secondary-menu--header {
  color: var(--s-700);
  display: flex;
  font-weight: var(--font-weight-bold);
  line-height: var(--space-normal);
  margin: var(--space-small) 0;
  padding: 0 var(--space-small);
}
.secondary-menu--title {
  color: var(--s-600);
  display: flex;
  font-weight: var(--font-weight-medium);
  line-height: var(--space-normal);
  margin: var(--space-small) 0;
  padding: 0 var(--space-small);
}
// .
</style>
